import {
  SidePanelDS,
  TextCapitalized,
  Spacer,
  StyledFlex,
  InputDS,
  triggerToast,
} from "@qivia/ui";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { DriversListType } from "../driversAPI";
import {
  updateDriverAnalyticCode1Async,
  updateDriverAnalyticCode2Async,
  selectDriverAnalyticCode1UpdatedStatus,
  selectDriverAnalyticCode2UpdatedStatus,
  selectDriverNewStatus,
  driversSlice,
} from "../driversSlice";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { selectCompany } from "../../homeSlice";

export const AnalyticCodesSection = (props: {
  isDriverDelete: boolean;
  driverAnalyticCode1: string | null;
  driverAnalyticCode2: string | null;
  driverUuid: string;
  driversList: DriversListType[];
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const driverAnalyticCode1UpdatedStatus = useAppSelector(
    selectDriverAnalyticCode1UpdatedStatus,
  );
  const driverAnalyticCode2UpdatedStatus = useAppSelector(
    selectDriverAnalyticCode2UpdatedStatus,
  );
  const driverNewStatus = useAppSelector(selectDriverNewStatus);
  const [analyticCode1, setAnalyticCode1] = useState<string>(
    props.driverAnalyticCode1 ?? "",
  );
  const [analyticCode2, setAnalyticCode2] = useState<string>(
    props.driverAnalyticCode2 ?? "",
  );

  const [displayError, setDisplayError] = useState<boolean>(false);

  const hasDuplicateAnalyticCode1: boolean = useMemo(() => {
    if (analyticCode1 === "") return false;
    if (analyticCode1 === props.driverAnalyticCode1) return false;
    return !!props.driversList.find(
      (driver) => driver.analyticCode1 === analyticCode1,
    );
  }, [analyticCode1, props.driverAnalyticCode1, props.driversList]);

  const hasDuplicateAnalyticCode2: boolean = useMemo(() => {
    if (analyticCode2 === "") return false;
    if (analyticCode2 === props.driverAnalyticCode2) return false;
    return !!props.driversList.find(
      (driver) => driver.analyticCode2 === analyticCode2,
    );
  }, [analyticCode2, props.driverAnalyticCode2, props.driversList]);

  const isValidAnalyticCode1Input: boolean = useMemo(() => {
    return !(props.isDriverDelete || hasDuplicateAnalyticCode1);
  }, [hasDuplicateAnalyticCode1, props.isDriverDelete]);

  const isValidAnalyticCode2Input: boolean = useMemo(() => {
    return !(props.isDriverDelete || hasDuplicateAnalyticCode2);
  }, [hasDuplicateAnalyticCode2, props.isDriverDelete]);

  const onSubmitCode1 = useCallback(() => {
    if (!company) return;
    setDisplayError(true);
    if (isValidAnalyticCode1Input) {
      void dispatch(
        updateDriverAnalyticCode1Async({
          driverUuid: props.driverUuid,
          analyticCode1: analyticCode1,
          companyUuid: company.uuid,
        }),
      );
      setDisplayError(false);
    }
  }, [
    dispatch,
    analyticCode1,
    isValidAnalyticCode1Input,
    company,
    props.driverUuid,
  ]);

  const onSubmitCode2 = useCallback(() => {
    if (!company) return;
    setDisplayError(true);
    if (isValidAnalyticCode2Input) {
      void dispatch(
        updateDriverAnalyticCode2Async({
          driverUuid: props.driverUuid,
          analyticCode2: analyticCode2,
          companyUuid: company.uuid,
        }),
      );
      setDisplayError(false);
    }
  }, [
    dispatch,
    analyticCode2,
    isValidAnalyticCode2Input,
    company,
    props.driverUuid,
  ]);

  const analyticCode1Error = useMemo(() => {
    return displayError && hasDuplicateAnalyticCode1
      ? t("drivers.sidePanel.analyticCode1.error.alreadyExists")
      : undefined;
  }, [displayError, hasDuplicateAnalyticCode1, t]);

  const analyticCode2Error = useMemo(() => {
    return displayError && hasDuplicateAnalyticCode2
      ? t("drivers.sidePanel.analyticCode2.error.alreadyExists")
      : undefined;
  }, [displayError, hasDuplicateAnalyticCode2, t]);

  useEffect(() => {
    if (driverAnalyticCode1UpdatedStatus === "success") {
      triggerToast(t(`drivers.updatedAnalyticCode1.success`) || "", "valid");
    } else if (driverAnalyticCode1UpdatedStatus === "failed") {
      triggerToast(t("drivers.updatedAnalyticCode1.failure") || "", "error");
    }
    dispatch(driversSlice.actions.resetDriverUpdatedAnalyticCode1Status());
  }, [driverNewStatus, driverAnalyticCode1UpdatedStatus, t, dispatch]);

  useEffect(() => {
    if (driverAnalyticCode2UpdatedStatus === "success") {
      triggerToast(t(`drivers.updatedAnalyticCode2.success`) || "", "valid");
    } else if (driverAnalyticCode2UpdatedStatus === "failed") {
      triggerToast(t("drivers.updatedAnalyticCode2.failure") || "", "error");
    }
    dispatch(driversSlice.actions.resetDriverUpdatedAnalyticCode2Status());
  }, [driverNewStatus, driverAnalyticCode2UpdatedStatus, t, dispatch]);

  return (
    <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
      <StyledTitle>
        <TextCapitalized>
          {t("drivers.sidePanel.analyticCode1.title")}
        </TextCapitalized>
      </StyledTitle>
      <Spacer y={1.5} />
      <StyledFlex>
        <InputDS
          placeholder={t("drivers.sidePanel.analyticCode1.input.placeholder")}
          label={t("drivers.sidePanel.analyticCode1.input.title")}
          value={analyticCode1}
          onChange={(e) => setAnalyticCode1(e.target.value)}
          error={analyticCode1Error}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSubmitCode1();
              (e.target as HTMLInputElement).blur();
            }
          }}
          withoutSpacerBottom
          onBlur={() => onSubmitCode1()}
        />
      </StyledFlex>
      <Spacer y={1.5} />
      <StyledFlex>
        <InputDS
          placeholder={t("drivers.sidePanel.analyticCode2.input.placeholder")}
          label={t("drivers.sidePanel.analyticCode2.input.title")}
          value={analyticCode2}
          onChange={(e) => setAnalyticCode2(e.target.value)}
          error={analyticCode2Error}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSubmitCode2();
              (e.target as HTMLInputElement).blur();
            }
          }}
          withoutSpacerBottom
          onBlur={() => onSubmitCode2()}
        />
      </StyledFlex>
    </SidePanelDS.Section>
  );
};

const StyledTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;
